import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

// Session is held in the backend-set httpOnly `access_token` cookie — never in localStorage.
// On mount we ask the server who we are (cookie travels automatically via withCredentials).
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, object=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    // Unified signup — all client signups route through the same multi-field flow as Ronin Box.
    // Pass signup_source = "main_signup" to distinguish from "ronin_box" leads.
    const { data } = await api.post("/auth/register-ronin", {
      ...payload, signup_source: payload.signup_source || "main_signup",
    });
    setUser(data);
    return data;
  };

  const registerRonin = async (payload) => {
    const { data } = await api.post("/auth/register-ronin", payload);
    setUser(data);
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    return data;
  };

  const resetPassword = async (token, password) => {
    await api.post("/auth/reset-password", { token, password });
    await refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, registerRonin, forgotPassword, resetPassword, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
